import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Navigation, MapPin, Compass, AlertCircle } from 'lucide-react';

// Tirana coordinates for Qibla calculation
const KAABA_LAT = 21.4225;
const KAABA_LON = 39.8262;

const QiblaCompass = ({ isOpen, onClose }) => {
  const [qiblaDirection, setQiblaDirection] = useState(null);
  const [deviceHeading, setDeviceHeading] = useState(0);
  const [userLocation, setUserLocation] = useState(null);
  const [error, setError] = useState(null);
  const [permissionGranted, setPermissionGranted] = useState(false);
  const [loading, setLoading] = useState(true);

  // Calculate Qibla direction from user's location
  const calculateQibla = (lat, lon) => {
    const lat1 = lat * (Math.PI / 180);
    const lon1 = lon * (Math.PI / 180);
    const lat2 = KAABA_LAT * (Math.PI / 180);
    const lon2 = KAABA_LON * (Math.PI / 180);

    const dLon = lon2 - lon1;
    const y = Math.sin(dLon) * Math.cos(lat2);
    const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLon);
    let bearing = Math.atan2(y, x) * (180 / Math.PI);
    bearing = (bearing + 360) % 360;
    
    return bearing;
  };

  // Get user's location
  useEffect(() => {
    if (!isOpen) return;

    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setUserLocation({ lat: latitude, lon: longitude });
          const qibla = calculateQibla(latitude, longitude);
          setQiblaDirection(qibla);
          setLoading(false);
        },
        (err) => {
          console.error("Geolocation error:", err);
          // Use Tirana as default
          const defaultLat = 41.3247;
          const defaultLon = 19.8190;
          setUserLocation({ lat: defaultLat, lon: defaultLon });
          const qibla = calculateQibla(defaultLat, defaultLon);
          setQiblaDirection(qibla);
          setError("Duke përdorur vendndodhjen e Tiranës si parazgjedhje");
          setLoading(false);
        },
        { enableHighAccuracy: true }
      );
    } else {
      setError("Geolokacioni nuk mbështetet në këtë shfletues");
      setLoading(false);
    }
  }, [isOpen]);

  // Handle device orientation for compass
  useEffect(() => {
    if (!isOpen) return;

    const handleOrientation = (event) => {
      if (event.alpha !== null) {
        setDeviceHeading(event.alpha);
        setPermissionGranted(true);
      }
    };

    // Request permission for iOS 13+
    if (typeof DeviceOrientationEvent !== 'undefined' && 
        typeof DeviceOrientationEvent.requestPermission === 'function') {
      // iOS 13+ requires permission
      setPermissionGranted(false);
    } else {
      // Non-iOS or older iOS
      window.addEventListener('deviceorientation', handleOrientation);
      setPermissionGranted(true);
    }

    return () => {
      window.removeEventListener('deviceorientation', handleOrientation);
    };
  }, [isOpen]);

  const requestOrientationPermission = async () => {
    try {
      const permission = await DeviceOrientationEvent.requestPermission();
      if (permission === 'granted') {
        window.addEventListener('deviceorientation', (event) => {
          if (event.alpha !== null) {
            setDeviceHeading(event.alpha);
          }
        });
        setPermissionGranted(true);
      }
    } catch (err) {
      console.error("Permission error:", err);
      setError("Nuk u dha leja për kompasin");
    }
  };

  if (!isOpen) return null;

  // Calculate the rotation for the compass needle
  const needleRotation = qiblaDirection ? qiblaDirection - deviceHeading : 0;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
        data-testid="qibla-overlay"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          className="bg-white rounded-3xl max-w-md w-full overflow-hidden shadow-2xl"
          onClick={(e) => e.stopPropagation()}
          data-testid="qibla-modal"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-primary to-primary-600 p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-heading font-bold">Kibla</h2>
                <p className="text-white/70 text-sm">Drejtimi i Qabes</p>
              </div>
              <button
                onClick={onClose}
                className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center hover:bg-white/30"
                data-testid="qibla-close-btn"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-8">
            {loading ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-gray-600">Duke gjetur vendndodhjen...</p>
              </div>
            ) : (
              <>
                {/* Location Info */}
                <div className="flex items-center justify-center gap-2 mb-6 text-gray-600">
                  <MapPin className="w-5 h-5 text-accent" />
                  <span className="text-sm">
                    {userLocation ? `${userLocation.lat.toFixed(4)}°N, ${userLocation.lon.toFixed(4)}°E` : 'Tiranë, Shqipëri'}
                  </span>
                </div>

                {error && (
                  <div className="bg-amber-50 text-amber-700 p-3 rounded-xl mb-6 flex items-center gap-2 text-sm">
                    <AlertCircle className="w-5 h-5 flex-shrink-0" />
                    {error}
                  </div>
                )}

                {/* Compass */}
                <div className="relative w-64 h-64 mx-auto mb-6">
                  {/* Compass Base */}
                  <div className="absolute inset-0 rounded-full bg-gradient-to-br from-gray-100 to-gray-200 shadow-inner">
                    {/* Direction Labels */}
                    <span className="absolute top-2 left-1/2 -translate-x-1/2 text-sm font-bold text-gray-600">V</span>
                    <span className="absolute bottom-2 left-1/2 -translate-x-1/2 text-sm font-bold text-gray-600">J</span>
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-sm font-bold text-gray-600">P</span>
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-sm font-bold text-gray-600">L</span>
                  </div>

                  {/* Compass Ring */}
                  <svg className="absolute inset-2" viewBox="0 0 200 200">
                    {/* Degree marks */}
                    {Array.from({ length: 72 }).map((_, i) => (
                      <line
                        key={i}
                        x1="100"
                        y1="10"
                        x2="100"
                        y2={i % 6 === 0 ? "20" : "15"}
                        stroke="#9ca3af"
                        strokeWidth={i % 6 === 0 ? "2" : "1"}
                        transform={`rotate(${i * 5} 100 100)`}
                      />
                    ))}
                  </svg>

                  {/* Qibla Needle */}
                  <motion.div
                    className="absolute inset-0 flex items-center justify-center"
                    animate={{ rotate: needleRotation }}
                    transition={{ type: "spring", stiffness: 50, damping: 15 }}
                  >
                    <div className="relative w-full h-full">
                      {/* Kaaba Icon at needle tip */}
                      <div className="absolute top-4 left-1/2 -translate-x-1/2 w-12 h-12 bg-primary rounded-lg flex items-center justify-center shadow-lg">
                        <span className="text-white text-2xl">🕋</span>
                      </div>
                      {/* Needle line */}
                      <div className="absolute top-16 left-1/2 w-1 h-16 bg-gradient-to-b from-primary to-primary/50 -translate-x-1/2 rounded-full"></div>
                    </div>
                  </motion.div>

                  {/* Center dot */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-4 h-4 bg-accent rounded-full shadow-lg"></div>
                  </div>
                </div>

                {/* Qibla Degree */}
                <div className="text-center mb-6">
                  <p className="text-sm text-gray-500 mb-1">Drejtimi i Kiblës</p>
                  <p className="text-4xl font-heading font-bold text-primary">
                    {qiblaDirection ? `${qiblaDirection.toFixed(1)}°` : '--°'}
                  </p>
                  <p className="text-sm text-gray-500 mt-1">nga Veriu</p>
                </div>

                {/* Permission Button for iOS */}
                {!permissionGranted && typeof DeviceOrientationEvent !== 'undefined' && 
                 typeof DeviceOrientationEvent.requestPermission === 'function' && (
                  <button
                    onClick={requestOrientationPermission}
                    className="w-full py-3 bg-primary text-white rounded-xl font-semibold hover:bg-primary-600 transition-colors flex items-center justify-center gap-2"
                    data-testid="qibla-permission-btn"
                  >
                    <Compass className="w-5 h-5" />
                    Aktivizo Kompasin
                  </button>
                )}

                {/* Instructions */}
                <div className="bg-gray-50 rounded-xl p-4 mt-4">
                  <p className="text-sm text-gray-600 text-center">
                    <Navigation className="w-4 h-4 inline mr-1" />
                    Mbaj telefonin horizontalisht dhe rrotulloje derisa shigjeta të tregojë 🕋
                  </p>
                </div>
              </>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default QiblaCompass;
